'use client';

import { useEffect, useRef } from 'react';
import {
  Box,
  Center,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  VStack,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useAppLayoutContent } from 'context/appLayoutContent';
import { useNetwork } from 'context/network';
import { Button } from '../Button';

interface Props {
  isOpen: boolean;
  onClose(): void;
}

export const SplashScreen = (props: Props) => {
  const { isOpen, onClose } = props;

  const { network } = useNetwork();

  const { appLayoutContent, wasSplashScreenClosed, handleCloseSplashScreen } =
    useAppLayoutContent();

  const handleClose = () => {
    onClose();
    handleCloseSplashScreen();
  };

  const closeSplashScreenTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (closeSplashScreenTimer.current)
      clearTimeout(closeSplashScreenTimer.current);

    if (!isOpen) return;

    closeSplashScreenTimer.current = setTimeout(
      handleClose,
      12 * 1000, // 12 seconds
    );

    return () => {
      if (closeSplashScreenTimer.current)
        clearTimeout(closeSplashScreenTimer.current);
    };
  }, [isOpen]);

  const { splashScreen } = appLayoutContent ?? {};

  const { welcomeImageUrl, closeButton } = splashScreen ?? {};

  const isSplashScreenOpen = isOpen && !wasSplashScreenClosed;

  const getGiftcardUrl = () => {
    if (!network || !network?.giftcardFrontPageUrl) return '';

    const url = new URL(network.giftcardFrontPageUrl);

    url.searchParams.set('width', '2126');
    url.searchParams.set('height', '1370');

    return url.href;
  };

  return (
    <Modal
      onClose={handleClose}
      size="full"
      isOpen={isSplashScreenOpen}
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent bg="dynamic.highlight">
        <ModalBody as={VStack} px="0.75rem" justify="center">
          <Center>
            <Image
              src={welcomeImageUrl}
              as={motion.img}
              role="button"
              onClick={handleClose}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              w="8.125rem"
              alt="Welcome"
            />
          </Center>

          <Box __css={{ perspective: '850px' }} w="80vw">
            <Image
              src={getGiftcardUrl()}
              as={motion.img}
              role="button"
              onClick={handleClose}
              initial={{ rotateX: 11, rotateZ: -19, rotateY: 20 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              mt="4rem"
              w="full"
              maxW="25rem"
              mx="auto"
              shadow="0 0 1.25rem #00000065"
              rounded="xl"
              alt="Giftcard"
            />
          </Box>

          <Flex mt="auto">
            <Button
              variant="unstyled"
              onClick={handleClose}
              mt="7rem"
              fontSize="0.75rem"
              color="white"
              fontWeight="400"
              textDecoration="underline"
              _focus={{ boxShadow: 'none' }}
            >
              {closeButton}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
